// Los datos del producto que se ven y se editan en "Editar producto", debajo de las fotos
// (ADR-2026-10-21-editar-producto-muestra-sus-datos). Viven en dos tablas:
//
//   • variantes.descripcion_visual → cómo ve la IA el producto (la escribe la categorización
//     al mirar la foto principal; la leen los escritores de prompts al generar).
//   • variantes.descripcion        → el texto de la tienda (lo trae el import de Tiendanube).
//   • variantes.sku                → el código de la persona; las importaciones lo usan para no
//                                    duplicar productos (atlas-backend db/duplicados.ts).
//   • variantes.atributos          → las características, líneas "Clave: valor" (fichaDeProducto).
//   • productos.categoria_producto → el tipo, sólo en el catálogo de productos (no en moda).
//   • productos.medidas            → alto/ancho/profundidad; las que toca la persona quedan
//                                    `origen: 'usuario'` y nadie las pisa (ADR-583).
//
// Puro: la lectura y la escritura contra Supabase están en `useDetallesDelProducto`.
import { atributosDesdeFicha, fichaDesdeAtributos } from './fichaDeProducto';
import { normalizarMedidas, parsearMedida, type Medidas, type UnidadMedida } from './medidas';

/**
 * El "Tipo" que usa la IA (`productos.categoria_producto`): la whitelist del backend
 * (`CATEGORIA_PRODUCTO_WHITELIST`, services/categorize/logic.ts). Lo pone la categorización y no se
 * muestra (ADR-2026-10-22-una-categoria-por-producto-y-el-tipo-oculto): de él dependen las medidas
 * y la infografía. La categoría de la persona, con subcategorías, es otra cosa (`categorias_tienda`).
 */
export const CATEGORIAS_DE_PRODUCTO = [
  { id: 'mueble', etiqueta: 'Mueble' },
  { id: 'superficie', etiqueta: 'Pisos y revestimientos' },
  { id: 'electrodomestico', etiqueta: 'Electrodoméstico' },
  { id: 'iluminacion', etiqueta: 'Iluminación' },
  { id: 'herramienta', etiqueta: 'Herramienta' },
  { id: 'decoracion', etiqueta: 'Decoración' },
  { id: 'otro', etiqueta: 'Otro' },
] as const;

export type CategoriaDeProducto = (typeof CATEGORIAS_DE_PRODUCTO)[number]['id'];

const esCategoria = (v: unknown): v is CategoriaDeProducto =>
  CATEGORIAS_DE_PRODUCTO.some((c) => c.id === v);

export const MAX_LARGO_DESCRIPCION = 2000;
/** El mismo tope que valida el backend para el SKU de Tiendanube (routes/tiendanube.ts). */
export const MAX_LARGO_SKU = 120;

export interface MedidasEscritas {
  unidad: UnidadMedida;
  /** Como las escribe la persona ("60", "60,5"): se pasan a número al guardar. */
  alto: string;
  ancho: string;
  profundidad: string;
}

export interface DetallesDelProducto {
  sku: string;
  descripcionVisual: string;
  descripcion: string;
  /** El Tipo de la IA (`productos.categoria_producto`). */
  categoria: CategoriaDeProducto | null;
  /**
   * La categoría de la marca del producto (`categorias_tienda`): UNA, categoría o subcategoría.
   * Estar en "Oversize" ya es estar en "Remeras › Oversize".
   */
  categoriaPropia: string | null;
  /** Una línea por característica: "Material: roble" o "Glitter". */
  caracteristicas: string[];
  medidas: MedidasEscritas;
  /** `model_view === 'producto'`: el catálogo de productos (categoría y medidas). En moda, no. */
  esCatalogo: boolean;
  /** Lo guardado tal cual, para no perder el espesor y el peso al reescribir las medidas. */
  medidasGuardadas: Medidas | null;
}

export interface FilaDeVariante {
  sku?: string | null;
  descripcion?: string | null;
  descripcion_visual?: string | null;
  atributos?: unknown;
}

export interface FilaDeProducto {
  categoria_producto?: string | null;
  medidas?: unknown;
  model_view?: string | null;
}

const texto = (v: unknown): string => (typeof v === 'string' ? v : '');
const numeroEscrito = (n: number | null | undefined): string =>
  typeof n === 'number' && Number.isFinite(n) ? String(n).replace('.', ',') : '';

/** Las filas de la base → lo que muestra el formulario. */
export function detallesDesdeFilas(
  variante: FilaDeVariante | null,
  producto: FilaDeProducto | null,
  categoriaPropia: string | null = null,
): DetallesDelProducto {
  const medidas = normalizarMedidas(producto?.medidas);
  const caracteristicas = Array.isArray(variante?.atributos)
    ? (variante!.atributos as unknown[])
        .filter((x): x is string => typeof x === 'string' && x.trim().length > 0)
        .map((x) => x.trim())
    : [];
  return {
    sku: texto(variante?.sku),
    descripcionVisual: texto(variante?.descripcion_visual),
    descripcion: texto(variante?.descripcion),
    categoria: esCategoria(producto?.categoria_producto) ? producto!.categoria_producto as CategoriaDeProducto : null,
    categoriaPropia,
    caracteristicas,
    medidas: {
      unidad: medidas?.unidad ?? 'cm',
      alto: numeroEscrito(medidas?.alto),
      ancho: numeroEscrito(medidas?.ancho),
      profundidad: numeroEscrito(medidas?.profundidad),
    },
    esCatalogo: producto?.model_view === 'producto',
    medidasGuardadas: medidas,
  };
}

/** Las características como se guardan: sin vacías ni repetidas, "Clave: valor", tope 20. */
export function caracteristicasParaGuardar(lineas: string[]): string[] {
  return atributosDesdeFicha(fichaDesdeAtributos(lineas));
}

const mismasLineas = (a: string[], b: string[]): boolean =>
  a.length === b.length && a.every((x, i) => x.trim() === b[i].trim());

const mismasMedidas = (a: MedidasEscritas, b: MedidasEscritas): boolean =>
  a.unidad === b.unidad &&
  parsearMedida(a.alto) === parsearMedida(b.alto) &&
  parsearMedida(a.ancho) === parsearMedida(b.ancho) &&
  parsearMedida(a.profundidad) === parsearMedida(b.profundidad);

/** Las medidas escritas → el jsonb de `productos.medidas`. null si no queda ningún dato. */
export function medidasParaGuardar(
  escritas: MedidasEscritas,
  guardadas: Medidas | null,
  ahora: string,
): Medidas | null {
  const m: Medidas = {
    ...(guardadas ?? {}),
    unidad: escritas.unidad,
    alto: parsearMedida(escritas.alto),
    ancho: parsearMedida(escritas.ancho),
    profundidad: parsearMedida(escritas.profundidad),
    origen: 'usuario',
    actualizado: ahora,
  };
  const algo = [m.alto, m.ancho, m.profundidad, m.espesor, m.peso_kg].some((v) => typeof v === 'number');
  return algo ? m : null;
}

export interface CambiosDeDetalles {
  variante: {
    sku?: string | null;
    descripcion_visual?: string | null;
    descripcion?: string | null;
    atributos?: string[];
  };
  producto: { categoria_producto?: CategoriaDeProducto | null; medidas?: Medidas | null };
  /** La categoría nueva del producto (`productos_categorias`); null = sin categoría. */
  categoriaPropia?: { nueva: string | null };
  /** La persona tocó cómo ve la IA el producto: la categorización no la puede volver a escribir. */
  descripcionVisualEditada: boolean;
}

const textoParaGuardar = (v: string): string | null => {
  const t = v.trim().slice(0, MAX_LARGO_DESCRIPCION);
  return t ? t : null;
};

/** Sólo lo que cambió: lo que no se tocó no se reescribe (ni sella `origen: 'usuario'`). */
export function cambiosDeDetalles(
  original: DetallesDelProducto,
  actual: DetallesDelProducto,
  ahora: string = new Date().toISOString(),
): CambiosDeDetalles {
  const cambios: CambiosDeDetalles = { variante: {}, producto: {}, descripcionVisualEditada: false };
  if (actual.sku.trim() !== original.sku.trim()) {
    const sku = actual.sku.trim().slice(0, MAX_LARGO_SKU);
    cambios.variante.sku = sku || null;
  }
  if (actual.descripcionVisual.trim() !== original.descripcionVisual.trim()) {
    cambios.variante.descripcion_visual = textoParaGuardar(actual.descripcionVisual);
    cambios.descripcionVisualEditada = true;
  }
  if (actual.descripcion.trim() !== original.descripcion.trim()) {
    cambios.variante.descripcion = textoParaGuardar(actual.descripcion);
  }
  if (!mismasLineas(actual.caracteristicas, original.caracteristicas)) {
    cambios.variante.atributos = caracteristicasParaGuardar(actual.caracteristicas);
  }
  if (actual.categoria !== original.categoria) {
    cambios.producto.categoria_producto = actual.categoria;
  }
  if (actual.categoriaPropia !== original.categoriaPropia) {
    cambios.categoriaPropia = { nueva: actual.categoriaPropia };
  }
  if (!mismasMedidas(actual.medidas, original.medidas)) {
    cambios.producto.medidas = medidasParaGuardar(actual.medidas, original.medidasGuardadas, ahora);
  }
  return cambios;
}

export const hayCambios = (c: CambiosDeDetalles): boolean =>
  Object.keys(c.variante).length > 0 || Object.keys(c.producto).length > 0 || !!c.categoriaPropia;

/** Si su categoría se borró desde el panel (el vínculo se fue en cascada), queda sin categoría. */
export function sinCategoriasBorradas(d: DetallesDelProducto, existentes: ReadonlySet<string>): DetallesDelProducto {
  return d.categoriaPropia && !existentes.has(d.categoriaPropia) ? { ...d, categoriaPropia: null } : d;
}

/**
 * ¿Se vuelve a categorizar al tocar "Listo"? En modo editar la categorización sólo mira las
 * FOTOS (describe la principal y clasifica la vista de cada una): sin fotos nuevas no hay nada
 * que aprender. Y si la persona corrigió la descripción, gana ella: la categorización la pisaría.
 */
export function hayQueVolverADescribir(p: { fotosCambiaron: boolean; descripcionVisualEditada: boolean }): boolean {
  return p.fotosCambiaron && !p.descripcionVisualEditada;
}
